import { useEffect, useState } from 'react'
import { Transition } from '@headlessui/react'
import { BotProtectionWidget } from '@emporion/ui'
import { LoginCredentialsPanel, SignupStepActions } from './form-sections'
import { buildFieldUiHelpers } from './CustomerAuthForm.helpers'
import { CustomerAuthSignupStepContent } from './CustomerAuthSignupStepContent'
import type { CustomerAuthFormProps } from './CustomerAuthForm.types'
import { StepIndicator } from './StepIndicator'
import { SignupSuccessState } from './SignupSuccessState'

export const CustomerAuthForm = (props: CustomerAuthFormProps) => {
  const [loginFeedback, setLoginFeedback] = useState<'idle' | 'error'>('idle')
  const [signupFeedback, setSignupFeedback] = useState<'idle' | 'success'>('idle')
  const [botProtectionFeedback, setBotProtectionFeedback] = useState<'idle' | 'required'>('idle')
  const [botProtectionResetNonce, setBotProtectionResetNonce] = useState(0)
  const fieldUi = buildFieldUiHelpers({
    fieldErrors: props.fieldErrors,
    onFieldBlur: props.onFieldBlur,
  })

  const signupSteps = [
    { number: 1, label: props.signupStepPersonalLabel },
    { number: 2, label: props.signupStepContactLabel },
    { number: 3, label: props.signupStepSecurityLabel },
    { number: 4, label: props.signupStepReviewLabel },
  ]
  const formClassName = props.isSignup ? 'mt-1 space-y-2.5' : 'mt-1 space-y-2.5'
  const requiredLegendClassName = props.isSignup
    ? 'text-[11px] font-medium text-(--color-text-muted)'
    : 'text-[11px] font-normal text-(--color-text-muted)/80'
  const botProtectionToken = props.botProtectionToken
  const onBotProtectionTokenChange = props.onBotProtectionTokenChange
  const showBotProtection =
    props.isSignup &&
    props.signupStep >= props.totalSignupSteps &&
    props.botProtectionEnabled &&
    Boolean(props.botProtectionProviderId) &&
    Boolean(props.botProtectionClientConfig)
  const botProtectionDevelopmentMode = props.botProtectionClientConfig?.developmentMode === true
  const botProtectionUsingTestCredentials = props.botProtectionClientConfig?.usingTestCredentials === true
  const botProtectionRelaxedHostname = props.botProtectionClientConfig?.hostnameValidation === 'relaxed'

  useEffect(() => {
    if (showBotProtection || !botProtectionToken.trim()) return
    onBotProtectionTokenChange('')
    setBotProtectionFeedback('idle')
  }, [botProtectionToken, onBotProtectionTokenChange, showBotProtection])

  if (props.isSignup && signupFeedback === 'success') {
    return (
      <div className="pt-2">
        <SignupSuccessState
          title={props.signupSuccessTitleLabel}
          description={props.signupSuccessDescriptionLabel}
          loginCtaLabel={props.signupSuccessLoginCtaLabel}
          homeCtaLabel={props.signupSuccessHomeCtaLabel}
          onGoToLogin={props.onGoToLogin}
          onGoToHome={props.onGoToHome}
        />
      </div>
    )
  }

  return (
    <form
      className={formClassName}
      onSubmit={async (event) => {
        event.preventDefault()
        if (showBotProtection && !props.botProtectionToken.trim()) {
          setBotProtectionFeedback('required')
          return
        }
        if (props.isSignup && props.signupStep < props.totalSignupSteps) {
          setSignupFeedback('idle')
          setBotProtectionFeedback('idle')
          props.onNextSignupStep()
          return
        }
        const result = await props.onSubmit()
        if (!props.isSignup) {
          if (result === 'login_success') setLoginFeedback('idle')
          if (result === 'login_two_factor_required') setLoginFeedback('idle')
          if (result === 'login_error') setLoginFeedback('error')
        } else {
          if (result === 'signup_success') {
            setSignupFeedback('success')
          } else if (showBotProtection) {
            props.onBotProtectionTokenChange('')
            setBotProtectionResetNonce((current) => current + 1)
          }
        }
      }}
    >
      {props.isSignup ? <StepIndicator steps={signupSteps} currentStep={props.signupStep} /> : null}
      {props.isSignup ? <p className={requiredLegendClassName}>{props.requiredFieldLegendLabel}</p> : null}

      {props.isSignup ? (
        <CustomerAuthSignupStepContent signupStep={props.signupStep} form={props} ui={fieldUi} />
      ) : (
        <div className="space-y-2.5">
          <div className="space-y-1">
            <h2 className="text-base font-semibold tracking-tight text-(--color-text-primary)">
              {props.loginCardTitleLabel}
            </h2>
            <p className="text-sm text-(--color-text-secondary)">{props.loginCardSubtitleLabel}</p>
          </div>

          <LoginCredentialsPanel
            identifierLabel={props.loginIdentifierLabel}
            identifierPlaceholder={props.loginIdentifierPlaceholder}
            passwordLabel={props.passwordLabel}
            passwordPlaceholder={props.passwordPlaceholder}
            identifier={props.email}
            password={props.password}
            onIdentifierChange={(value) => {
              setLoginFeedback('idle')
              props.onEmailChange(value)
            }}
            onPasswordChange={(value) => {
              setLoginFeedback('idle')
              props.onPasswordChange(value)
            }}
            forgotPasswordLabel={props.forgotPasswordLabel}
            onForgotPassword={props.onForgotPassword}
            passwordShowLabel={props.passwordShowLabel}
            passwordHideLabel={props.passwordHideLabel}
            ui={fieldUi}
          />

          <p className={requiredLegendClassName}>{props.requiredFieldLegendLabel}</p>

          <Transition
            show={loginFeedback === 'error'}
            enter="transition duration-150 ease-out"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-120 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <p
              role="alert"
              aria-live="polite"
              className="rounded-lg border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-xs font-medium text-(--color-status-error)"
            >
              {props.loginInvalidFeedbackLabel}
            </p>
          </Transition>
        </div>
      )}

      {showBotProtection && props.botProtectionProviderId && props.botProtectionClientConfig ? (
        <div className="space-y-2 rounded-lg bg-[color-mix(in_srgb,var(--color-bg-page-secondary)_72%,var(--color-bg-card))] p-3">
          <p className="text-xs font-medium text-(--color-text-secondary)">{props.signupBotProtectionHelperLabel}</p>
          {botProtectionDevelopmentMode ? (
            <p className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px] text-sky-800">
              Development environment detected.
              {botProtectionUsingTestCredentials ? ' Using Turnstile test credentials.' : ''}
              {botProtectionRelaxedHostname ? ' Hostname validation relaxed for local development.' : ''}
            </p>
          ) : null}
          <BotProtectionWidget
            app="shop"
            providerId={props.botProtectionProviderId}
            clientConfig={props.botProtectionClientConfig}
            resetNonce={botProtectionResetNonce}
            containerName="customer-auth"
            onTokenChange={(token) => {
              setBotProtectionFeedback('idle')
              props.onBotProtectionTokenChange(token)
            }}
          />
          {botProtectionFeedback === 'required' ? (
            <p role="alert" className="text-xs font-medium text-(--color-status-error)">
              {props.signupBotProtectionRequiredLabel}
            </p>
          ) : null}
        </div>
      ) : null}

      {props.isSignup ? (
        <SignupStepActions
          busy={props.busy}
          initializing={props.initializing}
          signupStep={props.signupStep}
          totalSignupSteps={props.totalSignupSteps}
          previousStepLabel={props.previousStepLabel}
          nextStepLabel={props.nextStepLabel}
          signupCtaLabel={props.signupCtaLabel}
          onPreviousSignupStep={props.onPreviousSignupStep}
          onNextSignupStep={props.onNextSignupStep}
        />
      ) : (
        <div className="pt-1">
          <button
            type="submit"
            disabled={props.busy || props.initializing}
            className="ca-btn-primary inline-flex h-10 w-full items-center justify-center px-4 text-sm font-semibold shadow-[0_14px_26px_-22px_color-mix(in_srgb,var(--color-action-primary)_85%,transparent)] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {props.loginCtaLabel}
          </button>
          <p className="mt-1.5 text-center text-[11px] font-medium text-(--color-text-muted)">
            {props.loginSecureAccessLabel}
          </p>
        </div>
      )}

    </form>
  )
}
